import { cloneDeep, pick, sum } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { Message } from '@meitu/saas-ui';
import { getFramework } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import {
    BaseCells,
    CellStyle,
    ILayerBehavior,
    ITableLayer,
    MIN_FONT_SIZE,
    ResizeOperateType,
    ScaleOperateType,
} from '@/version-types';
import { MIN_BASE_CELL_HEIGHT, MIN_BASE_CELL_WIDTH, MIN_BORDER_WIDTH } from '../default-configs';
import { checkTableNumberIsValid, getFixedTableNumber } from '../utils/fix-number';
import { getScaledTableLayer, hoverAreaClassName, MinSizeInfoByScale } from '../utils/table';

type Behaviors = Pick<
    ILayerBehavior,
    | 'scaleOperateStyle'
    | 'resizeOperateStyle'
    | 'onBeforeScaleUpdate'
    | 'onBaseScaleEnd'
    | 'disableScaleAbsorb'
    | 'onBeforeResizeUpdate'
    | 'onBaseResizeEnd'
    | 'onBeforeCrop'
    | 'onBeforeAddOrUpdate'
    | 'isDefaultUnequalStretch'
    | 'checkIsInOperateArea'
>;

export const createOperationBehaviors = (frameworkIdentity: string) => {
    // TODO: 缩放过程中不能只存一份缩放前的状态, 因为多选多个表格图层并缩放时, 状态就会串掉. 但是目前没发现什么bug, 就先不改
    /** 记录resize或scale最初始时的baseCells */
    let originalBaseCells: BaseCells | null = null;
    /** 记录resize或scale过程中, 表格最小尺寸 */
    let minSizeInfo: MinSizeInfoByScale | null = null;
    /** 记录resize过程中, 表格高度缩放到的最小值, 用于放大时恢复原状 (issue: https://jira.meitu.com/browse/MTXXPRO-30355) */
    let tableMinHeightByResizing: number | null = null;
    // let debugPreScaleTime = performance.now();

    // TODO: 暂时用hack的方式实现
    const onResizeOrScaleEnd = async (layerId: string): Promise<boolean> => {
        const framework = getFramework(frameworkIdentity);
        const layer = framework.getLayer(layerId) as ITableLayer | undefined;
        if (!layer) return false;
        const newLayer = getFixedTableNumber(layer);
        framework.stage.addOrUpdate(newLayer);
        return true;
    };

    const behaviors: Behaviors = {
        scaleOperateStyle: () => ({
            hiddenBtns: [ScaleOperateType.LT, ScaleOperateType.LB, ScaleOperateType.RT],
        }),

        resizeOperateStyle: () => ({
            hiddenBtns: [ResizeOperateType.TC, ResizeOperateType.LC],
            isSimpleBtn: true,
        }),

        onBeforeAddOrUpdate(_layer) {
            const layer = _layer as ITableLayer;

            layer.data.cells.flat().forEach((cell) => {
                if (!cell) return;
                if (!cell.__renderId) {
                    cell.__renderId = uuidv4();
                }
            });

            if (!checkTableNumberIsValid(layer) && !layer.__runtime?.disableAutoDataFix) {
                // 防bug的兜底逻辑: 精度修复, 防止单元格边框处出现空白间隙问题
                // 除了历史遗留的脏数据图层. 正常情况下不应该触发这个兜底逻辑, 如果触发, 需要排查一下, 否则频繁触发的话会导致布局抖动问题 (如: https://jira.meitu.com/browse/MTXXPRO-60169)
                const newLayer = getFixedTableNumber(layer);
                console.log('===⚠️⚠️⚠️表格数据精度修复', {
                    oldLayout: {
                        size: `w: ${layer.width}; h: ${layer.height}`,
                        hSies: layer.data.baseCells.horizontal,
                        vSies: layer.data.baseCells.vertical,
                    },
                    newLayout: {
                        size: `w: ${newLayer.width}; h: ${newLayer.height}`,
                        hSies: newLayer.data.baseCells.horizontal,
                        vSies: newLayer.data.baseCells.vertical,
                    },
                });
                // 下面的操作相当于把 newLayer 赋值给 layer
                Object.keys(layer).forEach((key) => delete layer[key]);
                Object.assign(layer, newLayer);
            }
        },

        // 🔔🔔🔔 大表格重排时间会特别久, 无法优化 (150ms左右就会卡顿了)
        onBeforeScaleUpdate(
            // expectlayer是按照通用的图层的逻辑走, 下一帧应该获得的图层数据. 这里用他去获取次帧鼠标移动的偏移值
            _expectlayer,
            { previousTickLayer: _previousTickLayer, isStart, isFromAdjustScale },
        ) {
            const expectlayer = _expectlayer as ITableLayer;
            const previousTickLayer = _previousTickLayer as ITableLayer;

            const { $t } = getLocaleStore();
            if (isStart) {
                minSizeInfo = null;
                originalBaseCells = previousTickLayer.data.baseCells;
            }

            const newPosition = pick(expectlayer, ['top', 'left']);
            const deltaOfWidth = expectlayer.width - previousTickLayer.width;
            const deltaOfHeight = expectlayer.height - previousTickLayer.height;
            let newLayer = cloneDeep(previousTickLayer);
            if (isFromAdjustScale) {
                const scale = expectlayer.width / previousTickLayer.width;
                for (const styleId in newLayer.data.styles) {
                    const style = newLayer.data.styles[styleId];
                    if (style.fontSize) {
                        style.fontSize = Math.max(MIN_FONT_SIZE, Math.round(style.fontSize * scale));
                    }
                    if (style.border) {
                        Object.values(style.border).forEach((borderItem) => {
                            borderItem.width = Math.max(MIN_BORDER_WIDTH, Math.round(borderItem.width * scale));
                        });
                    }
                }
                const originalFirstCellStyle = [
                    previousTickLayer.data.allStyleId,
                    ...previousTickLayer.data.cells[0][0]!.styleIds,
                ].reduce<CellStyle>(
                    (res, styleId) => ({
                        ...res,
                        ...previousTickLayer.data.styles[styleId],
                    }),
                    {},
                );
                const originalFakeBorderTop = (originalFirstCellStyle.border?.top?.width ?? 0) / 2;
                const originalFakeBorderLeft = (originalFirstCellStyle.border?.left?.width ?? 0) / 2;
                const originalFakeBorder = {
                    top: originalFakeBorderTop,
                    left: originalFakeBorderLeft,
                    right:
                        previousTickLayer.width -
                        originalFakeBorderLeft -
                        sum(previousTickLayer.data.baseCells.horizontal),
                    bottom:
                        previousTickLayer.height -
                        originalFakeBorderTop -
                        sum(previousTickLayer.data.baseCells.vertical),
                };
                const newFakeBorder = {
                    top: originalFakeBorder.top * scale,
                    left: originalFakeBorder.left * scale,
                    right: originalFakeBorder.right * scale,
                    bottom: originalFakeBorder.bottom * scale,
                };
                newLayer.data.baseCells.horizontal = newLayer.data.baseCells.horizontal.map((size) =>
                    Math.max(MIN_BASE_CELL_WIDTH, size * scale),
                );
                newLayer.data.baseCells.vertical = newLayer.data.baseCells.vertical.map((size) =>
                    Math.max(MIN_BASE_CELL_HEIGHT, size * scale),
                );
                newLayer.width = sum(newLayer.data.baseCells.horizontal) + newFakeBorder.left + newFakeBorder.right;
                newLayer.height = sum(newLayer.data.baseCells.vertical) + newFakeBorder.top + newFakeBorder.bottom;
                // 文字撑开的话，高度可能不够，需要修复一下
                if (!newLayer.__runtime) newLayer.__runtime = {};
                newLayer.__runtime.shouldFixLayout = true;
            } else {
                // const newTime = performance.now();
                // console.log('===onBeforeScaleUpdate 间隔多久触发一次', newTime - debugPreScaleTime);
                // debugPreScaleTime = newTime;
                // console.time('===getScaledTableLayer');
                newLayer = getScaledTableLayer({
                    frameworkIdentity,
                    currentActiveTableLayer: previousTickLayer,
                    originalBaseCells: originalBaseCells!,
                    delta: {
                        deltaOfWidth: deltaOfWidth,
                        deltaOfHeight: deltaOfHeight,
                    },
                    onLimit: (direction) =>
                        Message.info(
                            direction === 'horizontal'
                                ? $t((v) => v.tableLayer.behavior.limitWidth)
                                : $t((v) => v.tableLayer.behavior.limitHeight),
                        ),
                    minSizeInfo: minSizeInfo ?? undefined,
                    onGetMinSizeInfo: (d) => (minSizeInfo = d),
                });
                // console.timeEnd('===getScaledTableLayer');
            }

            // 下面的操作相当于把 newLayer 赋值给 expectlayer
            Object.keys(expectlayer).forEach((key) => delete expectlayer[key]);
            Object.assign(expectlayer, newLayer, newPosition);
        },

        disableScaleAbsorb(_layer) {
            const layer = _layer as ITableLayer;
            return layer.data.baseCells.horizontal.length * layer.data.baseCells.vertical.length >= 20 * 20;
        },

        onBaseScaleEnd: onResizeOrScaleEnd,

        onBeforeResizeUpdate(layer, { previousTickLayer: _previousTickLayer, isChangeHeight, isStart }) {
            const { $t } = getLocaleStore();
            const previousTickLayer = _previousTickLayer as ITableLayer;
            if (isStart) {
                tableMinHeightByResizing = null;
                minSizeInfo = null;
                originalBaseCells = previousTickLayer.data.baseCells;
            }

            const newPosition = pick(layer, ['top', 'left']);
            const deltaOfWidth = layer.width - previousTickLayer.width;
            const deltaOfHeight = layer.height - previousTickLayer.height;
            let limitDirection: 'horizontal' | 'vertical' | null = null;
            // console.time('===getScaledTableLayer');
            let newLayer = getScaledTableLayer({
                frameworkIdentity,
                currentActiveTableLayer: previousTickLayer,
                originalBaseCells: originalBaseCells!,
                delta: {
                    deltaOfWidth: isChangeHeight ? undefined : deltaOfWidth,
                    deltaOfHeight: isChangeHeight ? deltaOfHeight : undefined,
                },
                onLimit: (direction) => (limitDirection = direction),
                minSizeInfo: minSizeInfo ?? undefined,
                onGetMinSizeInfo: (d) => (minSizeInfo = d),
            });
            // console.timeEnd('===getScaledTableLayer');

            /** 放大时恢复原状 (issue: https://jira.meitu.com/browse/MTXXPRO-30355) */
            const shouldRestore =
                layer.width > previousTickLayer.width &&
                tableMinHeightByResizing !== null &&
                newLayer.height > tableMinHeightByResizing;
            if (shouldRestore) {
                newLayer = getScaledTableLayer({
                    frameworkIdentity,
                    currentActiveTableLayer: previousTickLayer,
                    originalBaseCells: originalBaseCells!,
                    delta: {
                        deltaOfWidth,
                        deltaOfHeight: tableMinHeightByResizing! - previousTickLayer.height,
                    },
                    onLimit: (direction) => (limitDirection = direction),
                });
            }

            if (limitDirection) {
                Message.info(
                    limitDirection === 'horizontal'
                        ? $t((v) => v.tableLayer.behavior.limitWidth)
                        : $t((v) => v.tableLayer.behavior.limitHeight),
                );
            }

            // 下面的操作相当于把 newLayer 赋值给 layer
            Object.keys(layer).forEach((key) => delete layer[key]);
            Object.assign(layer, newLayer, newPosition);

            // 记录数据
            {
                if (
                    !isChangeHeight &&
                    (tableMinHeightByResizing === null || newLayer.height < tableMinHeightByResizing)
                ) {
                    tableMinHeightByResizing = newLayer.height;
                }
            }
        },

        onBaseResizeEnd: onResizeOrScaleEnd,

        isDefaultUnequalStretch: () => true,

        checkIsInOperateArea: (_, mouseRect) => {
            const isInOperateArea = document
                .elementsFromPoint(mouseRect.x, mouseRect.y)
                .some((node) => node.classList.contains(hoverAreaClassName));
            return isInOperateArea;
        },
    };

    return behaviors;
};
